import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Button, FileUploadZone, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useAssetLibrary, { useAssetLibraryForm } from '@hooks/useAssetLibrary'
import useUppyAttachmentUpload from '@hooks/useUppyAttachmentUpload'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import { confirmDelete } from '@functions/entity'

import EntitySelector from '@components/EntitySelector'
import ScopedConditionalContent from '@components/ScopedConditionalContent'

import AttachmentList from './components/AttachmentList'

const MODAL_KEY = 'CreateOrEditAssetLibraryModal'

type ModalCallbacks = {
  closeModal: () => void,
  createAssetLibrary: (payload: HandleCallbackActionParams) => void,
  updateAssetLibrary: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditAssetLibraryModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createAssetLibrary, updateAssetLibrary } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const assetLibrary = digObject(modalPayload, 'assetLibrary', {})

  const newRecord = !assetLibrary.id

  const assetLibraryPayload = useAssetLibrary(
    assetLibrary,
    {
      performHttpRequests: !newRecord,
      requestOptions: {
        include_asset_library_attachments: true,
        include_attachment_file: true,
      },
    },
  )
  const {
    callbacks: {
      createAssetLibrary: createFn,
      deleteAssetLibrary: deleteFn,
      updateAssetLibrary: updateFn,
    },
    creating,
    deleting,
    editable,
    updating,
  } = assetLibraryPayload

  const {
    entityState,
    entityState: {
      id,
      owner_id,
      owner_type,
      title,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useAssetLibraryForm(assetLibrary, { validateOn: 'change' })

  const confirmDeletePayload = {
    callbacks: {
      afterAction: closeModal,
      action: deleteFn,
    },
    strings: { entityName: 'Asset Library' },
  }

  const createOrUpdateAssetLibraryPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: { include_asset_library_attachments_count: true },
    toastText: `Document Library ${newRecord ? 'Created' : 'Updated'}`,
  }

  const attachmentParams = {
    attachable_id: id,
    attachable_type: 'AssetLibrary',
  }
  // @ts-expect-error TS2741
  const uppy = useUppyAttachmentUpload({ attachmentParams }, [...Object.values(attachmentParams)])

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create' : 'Edit'}
        titleSecondLine="Document Library"
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input disabled={!editable} name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
            <Form.Field label="* Owner Type" marginTop="large">
              {/*
               // @ts-expect-error TS2322 */}
              <select
                disabled={!newRecord}
                name="owner_type"
                value={owner_type}
                {...handlers}
              >
                <option value="">Please Select...</option>
                <option value="Agency">Agency</option>
                <option value="Brand">Brand</option>
              </select>
            </Form.Field>

            {owner_type === 'Agency' && (
              <EntitySelector
                callbacks={{
                  selectItem: agency => setEntityState({ owner_id: agency ? agency.id : null }),
                }}
                disabled={!newRecord}
                entityKey="agencies"
                errorMessage={errors.owner_id}
                label="Agency"
                marginTop="large"
                selectedItemId={owner_id}
              />
            )}

            {owner_type === 'Brand' && (
              <EntitySelector
                callbacks={{
                  selectItem: brand => setEntityState({ owner_id: brand ? brand.id : null }),
                }}
                disabled={!newRecord}
                entityKey="brands"
                errorMessage={errors.owner_id}
                label="Brand"
                marginTop="large"
                selectedItemId={owner_id}
              />
            )}
          </ScopedConditionalContent>

          {!newRecord && editable && (
            <Form.Field label="Upload File" marginTop="large">
              <FileUploadZone uppy={uppy} />
            </Form.Field>
          )}

          {/*
           // @ts-expect-error TS2322 */}
          {!newRecord && <AttachmentList attachableId={id} attachableType="AssetLibrary" editable={editable} />}

          {!newRecord && editable && (
            <Form.Field label="More Options" marginTop="large">
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                loading={deleting}
                // @ts-expect-error TS2345
                onClick={() => confirmDelete(confirmDeletePayload)}
                size="medium"
              >
                Delete Asset Library
              </Button>
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        {editable && (
          <Button
            buttonStyle="primaryCreate"
            disabled={!saveEnabled}
            loading={creating || updating}
            loadingBubbleColor="white"
            onClick={
              newRecord
                ? () => createAssetLibrary(createOrUpdateAssetLibraryPayload)
                : () => updateAssetLibrary(createOrUpdateAssetLibraryPayload)
            }
            size="large"
          >
            {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Asset Library` : 'Complete Fields'}
          </Button>
        )}

        {!editable && (
          <Button
            buttonStyle="secondaryUtility"
            onClick={() => closeModal()}
            size="large"
          >
            Close Modal
          </Button>
        )}
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditAssetLibraryModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
